import React from "react";
import { action } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import { Dropdown, Input, Calendar, Select, Row, Col } from "antd";

import { v1 as uuid } from "uuid";
import { translateKey as t } from "../../locales/translation";

import "./OFDatePicker.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import dayjs, { Dayjs } from "dayjs";

interface OFDatePickerProps {
  controller: any;
  selectedDates: Array<Dayjs>;
  // Optional
  disabledDropdown?: boolean;
  readOnly?: boolean;
  disabledItem?: (item) => boolean;
  placeholder?: string;
  btnPlaceholder?: string | JSX.Element;
  menuPlacement?: "bottomRight" | "topLeft" | "top" | "topRight" | "bottomLeft" | "bottom";
  size?: "small" | "middle" | "large";
  style?: object;
  allowClear?: boolean;
  icon?: JSX.Element;
  menuWidth?: number;
}

const OFDatePicker: React.FC<OFDatePickerProps> = observer(
  ({ controller, selectedDates, disabledDropdown, readOnly, disabledItem, placeholder, btnPlaceholder, menuPlacement, size, style, menuWidth, allowClear, icon }) => {
    const state = useLocalObservable(() => ({
      classId: uuid(),
      inputRef: null,
      dropDownActive: false,
      menuWidth: menuWidth || 200,
      selectedDates: selectedDates,
      //----------------------------------------------------
      setInputRef(value: any) {
        this.inputRef = value;
      },
      setDropDownActive(value: boolean) {
        this.dropDownActive = value;
      },
      setMenuWidth(value: number) {
        this.menuWidth = value;
      },
      setSelectedDates: action(function (newSelectedDates: Array<Dayjs>) {
        this.selectedDates = newSelectedDates;
      }),

      selectedMonth: dayjs().month(),
      selectedYear: dayjs().year(),

      setMonth(month) {
        this.selectedMonth = month;
      },
      setYear(year) {
        this.selectedYear = year;
      },
    }));

    React.useEffect(() => {
      const { setSelectedDates, setInputRef } = state;

      setSelectedDates(selectedDates);
      setInputRef(document.getElementById(state.classId));
    }, [state.classId, selectedDates, state]);
    //------------------------------------------------------------------------------------------------

    const controlDropDown = () => {
      if (state.dropDownActive) {
        state.setDropDownActive(false);
        state.setMonth(dayjs().month());
        state.setYear(dayjs().year());
        // state.inputRef.blur();
      } else {
        // state.inputRef.blur();
        state.setDropDownActive(true);
      }
    };

    // -------------Renders--------------------------------------------------------------------------------------

    const headerRenderNew = ({ value: calendarValue, onChange }) => {
      const months = Array.from({ length: 12 }, (_, index) => dayjs().month(index).format("MMMM"));
      const years = Array.from({ length: 2 }, (_, index) => calendarValue.year() + index);

      return (
        <div style={{ padding: 10 }}>
          <Row gutter={8} justify="end">
            <Col>
              <Select
                // value={calendarValue.month()}
                onChange={(month) => {
                  const newValue = calendarValue.month(month); // Update the month
                  onChange(newValue);
                  state.setMonth(month);
                }}
                size="small"
                popupMatchSelectWidth={false}
                className="my-year-select"
                value={state.selectedMonth}
              >
                {months.map((month, index) => (
                  <Select.Option key={index} value={index}>
                    {month}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                // value={calendarValue.year()}
                onChange={(year) => {
                  const newValue = calendarValue.year(year);
                  onChange(newValue);
                  state.setYear(year);
                }}
                size="small"
                popupMatchSelectWidth={false}
                className="my-year-select"
                value={state.selectedYear}
              >
                {years.map((year) => (
                  <Select.Option key={year} value={year}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      );
    };

    const handleSelectDate = (date: Dayjs) => {
      const formattedDate = date.startOf("day"); // Normalize the date
      const isSelected = selectedDates.some((selectedDate) => selectedDate.isSame(formattedDate));

      if (isSelected) {
        controller.setSelectedDates(selectedDates.filter((selectedDate) => !selectedDate.isSame(formattedDate)));
      } else {
        controller.setSelectedDates([...selectedDates, formattedDate]);
      }
    };

    const dateCellRender = (value: Dayjs) => {
      const isSelected = selectedDates.some((selectedDate) => selectedDate.isSame(value, "day"));

      return (
        <div
          onClick={() => handleSelectDate(value)}
          style={{
            backgroundColor: isSelected ? "#1890ff" : undefined,
            color: isSelected ? "white" : undefined,
            cursor: "pointer",
          }}
        >
          {value.date()}
        </div>
      );
    };

    const RenderDatePicker = observer(() => {
      return (
        <div className="dropDownOptions" style={{ width: state.menuWidth }}>
          <Calendar
            style={{}}
            headerRender={headerRenderNew}
            disabledDate={(current) => {
              const today = dayjs().startOf("day");
              const maxDate = today.add(17, "day");

              return current && (current < today || current > maxDate);
            }}
            fullscreen={false}
            dateFullCellRender={dateCellRender}
          />
        </div>
      );
    });

    return (
      <Dropdown
        className={readOnly ? "OFMultiSelect OFVirtualizedDropDown readOnly" : "OFMultiSelect OFVirtualizedDropDown"}
        dropdownRender={() => <RenderDatePicker />}
        trigger={["click"]}
        overlayStyle={{ top: 30, left: 2 }}
        overlayClassName="OFMultiSelect"
        placement={menuPlacement || "bottomLeft"}
        onOpenChange={() => controlDropDown()}
        open={state.dropDownActive}
        disabled={disabledDropdown}
        getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement}
      >
        <Input
          value={t("lblSelectedDaysCount", "Selected ({1}) Days", [selectedDates.length])}
          style={style || {}}
          size={size || "middle"}
          id={state.classId}
          suffix={<FontAwesomeIcon onClick={(e) => (disabledDropdown ? e.preventDefault() : controlDropDown())} icon={faAngleDown} className={"inputArrow"} />}
          prefix={icon}
          allowClear={allowClear || false}
          onClear={() => controller.setSelectedDates([])}
          disabled={disabledDropdown}
        />
      </Dropdown>
    );
  }
);
export { OFDatePicker };
